import { Badge, Spinner } from '@lensora/ui'
import * as React from 'react'

import { useMcpKeyUsage } from '@/hooks/use-mcp-keys'

// ─── Types ──────────────────────────────────────────────────────────────────

interface KeyUsageModalProps {
  keyId: string
  keyName: string
  onClose: () => void
}

const DAY_OPTIONS = [1, 7, 14, 30]

const STATUS_VARIANT = {
  ok: 'success',
  error: 'destructive',
  denied: 'destructive',
  rate_limited: 'warning',
} as const

// ─── Component ────────────────────────────────────────────────────────────────

export function KeyUsageModal({ keyId, keyName, onClose }: KeyUsageModalProps) {
  const [days, setDays] = React.useState(7)
  const { data, isLoading } = useMcpKeyUsage(keyId, days)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="max-h-[80vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-border bg-surface p-6 shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text">Usage — {keyName}</h2>
            <p className="mt-0.5 text-sm text-text-muted">Calls and credits spent through this key.</p>
          </div>
          <div className="flex gap-1">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                  days === d ? 'bg-brand-primary text-brand-primary-foreground' : 'text-text-muted hover:bg-surface-raised'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : !data ? (
          <p className="text-sm text-text-muted">No usage data available.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-border p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Calls</p>
                <p className="text-2xl font-bold tabular-nums text-text">{data.totals.calls}</p>
              </div>
              <div className="rounded-md border border-border p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Credits</p>
                <p className="text-2xl font-bold tabular-nums text-text">{data.totals.credits}</p>
              </div>
            </div>

            <div className="divide-y divide-border rounded-md border border-border">
              {data.calls.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-text-muted">No calls in this window.</p>
              ) : (
                data.calls.map((call) => (
                  <div key={call.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-text">{call.toolName}</p>
                      <p className="text-xs text-text-muted">{call.clientName ?? 'Unknown client'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs tabular-nums text-text-muted">{call.creditsCharged} cr</span>
                      <Badge variant={STATUS_VARIANT[call.status]}>{call.status}</Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
